import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, AuthContext } from './context/AuthContext';
import Login from './pages/Login';
import CampusSquare from './pages/CampusSquare';
import InstructionalCenter from './pages/InstructionalCenter';
import AdminPanel from './pages/AdminPanel';
import AdminPathManager from './pages/AdminPathManager';
import AdminCourseManager from './pages/AdminCourseManager';
import PathView from './pages/PathView';
import CourseView from './pages/CourseView';
import ModuleView from './pages/ModuleView';
import TaskView from './pages/TaskView';
import AdminTaskBuilder from './pages/AdminTaskBuilder';
import FinancialOffice from './pages/FinancialOffice';
import AcademicResourceCenter from './pages/AcademicResourceCenter';
import Apply from './pages/Apply';
import AdmissionsOffice from './pages/AdmissionsOffice';
import EnrollmentAgreement from './pages/EnrollmentAgreement';
import FacultyHall from './pages/FacultyHall';
import Lounge from './pages/Lounge';
import QuizView from './pages/QuizView';

// Protected route — only lets logged in users through
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useContext(AuthContext);
  const [agreementSigned, setAgreementSigned] = useState(null);

  useEffect(() => {
    const checkAgreement = async () => {
      if (user && user.role_id === 4) {
        try {
          const res = await axios.get('https://iia-backend-production.up.railway.app/api/admissions/agreement/status');
          setAgreementSigned(res.data.signed);
        } catch (err) {
          setAgreementSigned(true); // fail open
        }
      } else {
        setAgreementSigned(true); // admins/staff skip gate
      }
    };
    if (user) checkAgreement();
  }, [user]);

  if (loading || (user && agreementSigned === null)) {
    return <div style={{ minHeight: '100vh', backgroundColor: '#1A2744' }} />;
  }

  if (!user) return <Navigate to="/" />;

  if (user.role_id === 4 && agreementSigned === false) {
    return <Navigate to="/agreement" />;
  }

return children;
};

const AppRoutes = () => {
  const { user, loading } = useContext(AuthContext);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#1A2744',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#C49A28',
        fontSize: '18px',
        letterSpacing: '2px'
      }}>
        Loading Campus...
      </div>
    );
  }

  return (
    <Routes>
      {/* Public route — login page */}
      <Route 
        path="/" 
        element={user ? <Navigate to="/campus" /> : <Login />} 
      />
      
      {/* Protected routes — must be logged in */}
      <Route 
        path="/campus" 
        element={
          <ProtectedRoute>
            <CampusSquare />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/instructional" 
        element={
          <ProtectedRoute>
            <InstructionalCenter />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            <AdminPanel />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/path/:pathId" 
        element={
          <ProtectedRoute>
            <AdminPathManager />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/course/:courseId" 
        element={
          <ProtectedRoute>
            <AdminCourseManager />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/instructional/path/:pathId" 
        element={
          <ProtectedRoute>
            <PathView />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/instructional/course/:courseId" 
        element={
          <ProtectedRoute>
            <CourseView />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/instructional/module/:moduleId" 
        element={
          <ProtectedRoute>
            <ModuleView />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/instructional/task/:taskId" 
        element={
          <ProtectedRoute>
            <TaskView />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/task/:taskId" 
        element={
          <ProtectedRoute>
            <AdminTaskBuilder />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/financial" 
        element={
          <ProtectedRoute>
            <FinancialOffice />
          </ProtectedRoute>
        } 
      />
      <Route path="/academic" 
      element={
        <ProtectedRoute>
          <AcademicResourceCenter />
        </ProtectedRoute>} 
      />
      {/* Catch all — redirect to home */}
      {/* Public routes — no login required */}
      <Route path="/apply" element={<Apply />} />
      <Route path="/agreement" element={<EnrollmentAgreement />} />
      <Route path="/admissions" element={<ProtectedRoute><AdmissionsOffice /></ProtectedRoute>} />
      <Route path="/faculty" element={<ProtectedRoute><FacultyHall /></ProtectedRoute>} />
      <Route path="/lounge" element={<ProtectedRoute><Lounge /></ProtectedRoute>} />
      <Route path="/quiz/:moduleId" element={<ProtectedRoute><QuizView /></ProtectedRoute>} />

      {/* Catch all — redirect to home */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

const App = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;

